import { action, makeObservable, observable, runInAction } from 'mobx';
import { db as firestoreDb, fn } from 'firebase-modules';

import { FirestoreTeam, FirestoreTeamMember } from '@creative-kit/shared';
import type { RootStore } from '.';

interface TeamWithId extends FirestoreTeam {
  id: string;
}

export class UiTeamsStore {
  private rootStore: RootStore;

  teams: Record<string, TeamWithId[]> = {}; // organizationId -> teams[]
  teamMembers: Record<string, Record<string, FirestoreTeamMember[]>> = {}; // organizationId -> teamId -> members[]
  isLoading = false;
  isCreating = false;
  isUpdating = false;
  isDeleting = false;
  isAddingMember = false;
  isRemovingMember = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      teams: observable,
      teamMembers: observable,
      isLoading: observable,
      isCreating: observable,
      isUpdating: observable,
      isDeleting: observable,
      isAddingMember: observable,
      isRemovingMember: observable,
      error: observable,
      loadTeams: action.bound,
      createTeam: action.bound,
      updateTeam: action.bound,
      deleteTeam: action.bound,
      loadTeamMembers: action.bound,
      addTeamMember: action.bound,
      removeTeamMember: action.bound,
      getTeamsByOrganization: action.bound,
      getTeamMembersByTeam: action.bound,
    });

    this.rootStore = rootStore;
  }

  /**
   * Load teams for an organization
   */
  async loadTeams(organizationId: string) {
    this.isLoading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb
        .collection('organizations')
        .doc(organizationId)
        .collection('teams')
        .get();

      const teamsList: TeamWithId[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreTeam;
        teamsList.push({ ...data, id: doc.id, createdAt: data.createdAt });
      });

      // Sort by creation date (newest first)
      teamsList.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const bDate = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return bDate - aDate;
      });

      runInAction(() => {
        this.teams[organizationId] = teamsList;
        this.isLoading = false;
      });
    } catch (err: any) {
      const errorMessage = err?.message || err?.code || 'Failed to load teams';
      console.error('Error loading teams:', err);
      runInAction(() => {
        this.error = errorMessage;
        this.isLoading = false;
      });
    }
  }

  /**
   * Get teams for an organization (from cache or load if needed)
   */
  getTeamsByOrganization(organizationId: string): TeamWithId[] {
    return this.teams[organizationId] || [];
  }

  /**
   * Create a new team
   */
  async createTeam(organizationId: string, name: string): Promise<string> {
    if (this.isCreating) return ''; // Prevent double-clicks

    this.isCreating = true;
    this.error = null;

    try {
      const createTeamFn = fn.httpsCallable('createTeam');
      const result = await createTeamFn({
        organizationId,
        name,
      });

      const teamId = result.data.teamId;

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Team created successfully',
      });

      // Reload teams
      await this.loadTeams(organizationId);

      return teamId;
    } catch (err: any) {
      // Extract detailed error message from Firebase Functions error
      let errorMessage = 'Failed to create team';
      if (err?.code === 'permission-denied' || err?.code === 'PERMISSION_DENIED') {
        errorMessage =
          err?.message ||
          'You do not have permission to create teams. Only organization owners and admins can create teams.';
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.code) {
        errorMessage = `Error: ${err.code}`;
      }
      console.error('Error creating team:', err);
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isCreating = false;
      });
    }
  }

  /**
   * Update team name
   */
  async updateTeam(organizationId: string, teamId: string, name: string): Promise<void> {
    if (this.isUpdating) return; // Prevent double-clicks

    this.isUpdating = true;
    this.error = null;

    try {
      const updateTeamFn = fn.httpsCallable('updateTeam');
      const result = await updateTeamFn({
        organizationId,
        teamId,
        name,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Team updated successfully',
      });

      // Reload teams
      await this.loadTeams(organizationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update team';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  /**
   * Delete a team
   */
  async deleteTeam(organizationId: string, teamId: string): Promise<void> {
    if (this.isDeleting) return; // Prevent double-clicks

    this.isDeleting = true;
    this.error = null;

    try {
      const deleteTeamFn = fn.httpsCallable('deleteTeam');
      const result = await deleteTeamFn({
        organizationId,
        teamId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Team deleted successfully',
      });

      // Remove from cache
      runInAction(() => {
        if (this.teams[organizationId]) {
          this.teams[organizationId] = this.teams[organizationId].filter(
            (team) => team.id !== teamId,
          );
        }
        // Remove team members from cache
        if (this.teamMembers[organizationId]) {
          delete this.teamMembers[organizationId][teamId];
        }
      });

      // Reload teams
      await this.loadTeams(organizationId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete team';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isDeleting = false;
      });
    }
  }

  /**
   * Load team members for a specific team
   */
  async loadTeamMembers(organizationId: string, teamId: string) {
    this.error = null;

    try {
      const snapshot = await firestoreDb
        .collection('organizations')
        .doc(organizationId)
        .collection('teams')
        .doc(teamId)
        .collection('members')
        .get();

      const membersList: FirestoreTeamMember[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as FirestoreTeamMember;
        membersList.push({ ...data, userId: doc.id, joinedAt: data.joinedAt });
      });

      // Sort by join date (newest first)
      membersList.sort((a, b) => {
        const aDate = a.joinedAt instanceof Date ? a.joinedAt.getTime() : 0;
        const bDate = b.joinedAt instanceof Date ? b.joinedAt.getTime() : 0;
        return bDate - aDate;
      });

      runInAction(() => {
        if (!this.teamMembers[organizationId]) {
          this.teamMembers[organizationId] = {};
        }
        this.teamMembers[organizationId][teamId] = membersList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load team members';
      });
    }
  }

  /**
   * Get team members for a team (from cache)
   */
  getTeamMembersByTeam(organizationId: string, teamId: string): FirestoreTeamMember[] {
    return this.teamMembers[organizationId]?.[teamId] || [];
  }

  /**
   * Add a member to a team
   */
  async addTeamMember(organizationId: string, teamId: string, userId: string): Promise<void> {
    if (this.isAddingMember) return; // Prevent double-clicks

    this.isAddingMember = true;
    this.error = null;

    try {
      const addTeamMemberFn = fn.httpsCallable('addTeamMember');
      const result = await addTeamMemberFn({
        organizationId,
        teamId,
        userId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Member added to team successfully',
      });

      // Reload team members
      await this.loadTeamMembers(organizationId, teamId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add team member';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isAddingMember = false;
      });
    }
  }

  /**
   * Remove a member from a team
   */
  async removeTeamMember(organizationId: string, teamId: string, userId: string): Promise<void> {
    if (this.isRemovingMember) return; // Prevent double-clicks

    this.isRemovingMember = true;
    this.error = null;

    try {
      const removeTeamMemberFn = fn.httpsCallable('removeTeamMember');
      const result = await removeTeamMemberFn({
        organizationId,
        teamId,
        userId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Member removed from team successfully',
      });

      // Reload team members
      await this.loadTeamMembers(organizationId, teamId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove team member';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      runInAction(() => {
        this.error = errorMessage;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isRemovingMember = false;
      });
    }
  }
}
